import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, ClipboardList, EyeOff, Filter } from 'lucide-react';
import { PortalMentorTask, portalMentorService } from '../../../services/api';
import { EmptyState, fa, inputClass } from './MentorUI';

/** همان پالت وضعیت پرتال؛ رنگ هیچ‌وقت تنها حامل معنا نیست و همیشه برچسب دارد */
const STATUS: Array<{ key: string; label: string; color: string }> = [
  { key: 'NOT_STARTED', label: 'شروع نشده', color: '#94A3B8' },
  { key: 'IN_PROGRESS', label: 'در حال انجام', color: '#3B82C4' },
  { key: 'SUBMITTED', label: 'تحویل شده', color: '#7C3AED' },
  { key: 'UNDER_REVIEW', label: 'در حال بررسی', color: '#7C3AED' },
  { key: 'NEEDS_REVISION', label: 'نیازمند اصلاح', color: '#fab219' },
  { key: 'APPROVED', label: 'تأیید شده', color: '#0ca30c' },
  { key: 'OVERDUE', label: 'عقب‌افتاده', color: '#d03b3b' },
];

const dueLabel = (iso?: string | null) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  const days = Math.round(Math.abs(diff) / 86400000);
  if (diff < 0) return { text: fa(days) + ' روز از مهلت گذشته', overdue: true, soon: false };
  if (days === 0) return { text: 'مهلت امروز', overdue: false, soon: true };
  return { text: fa(days) + ' روز تا مهلت', overdue: false, soon: days <= 3 };
};

interface Props {
  onError: (err: any, fallback: string) => void;
}

/**
 * فهرست تمرین‌های دوره از دید منتور.
 *
 * منتور تسک نمی‌سازد و ویرایش نمی‌کند — این صفحه فقط پاسخ می‌دهد که چه چیزی
 * منتشر شده، چه چیزی هنوز پیش‌نویس است، و شرکت‌کنندگان «خودِ او» روی هرکدام
 * کجا ایستاده‌اند.
 */
export const MentorTasksTab: React.FC<Props> = ({ onError }) => {
  const [tasks, setTasks] = useState<PortalMentorTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTasks(await portalMentorService.tasks());
    } catch (err: any) {
      onError(err, 'دریافت تمرین‌ها انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const term = search.trim();
    return tasks
      .filter((task) => (onlyMine ? task.myAssigned > 0 : true))
      .filter((task) =>
        term ? task.title.includes(term) || (task.session?.title ?? '').includes(term) : true,
      );
  }, [tasks, search, onlyMine]);

  if (loading) {
    return <p className="py-10 text-center text-sm font-bold text-gray-400">در حال بارگذاری…</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="جستجوی عنوان تمرین یا جلسه…"
          className={inputClass + ' w-auto min-w-[240px] flex-1'}
        />
        <button
          type="button"
          onClick={() => setOnlyMine((v) => !v)}
          className={
            'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold transition ' +
            (onlyMine
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-300 text-gray-600 hover:border-gray-400')
          }
        >
          <Filter className="h-3.5 w-3.5" />
          فقط تمرین‌های گروه من
        </button>
        <span className="text-xs font-bold text-gray-500">{fa(visible.length)} تمرین</span>
      </div>

      {!visible.length ? (
        <EmptyState
          icon={<ClipboardList className="h-5 w-5" />}
          title="تمرینی پیدا نشد"
          body={
            onlyMine
              ? 'هیچ تمرینی هنوز به شرکت‌کنندگان شما تخصیص نیافته است.'
              : 'مدیریت دوره هنوز تمرینی نساخته است.'
          }
        />
      ) : (
        visible.map((task) => {
          const due = dueLabel(task.dueAt);
          const total = task.myAssigned;

          return (
            <div key={task.id} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-bold text-gray-900">{task.title}</p>
                    {!task.isPublished ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10.5px] font-bold text-gray-500">
                        <EyeOff className="h-3 w-3" />
                        پیش‌نویس
                      </span>
                    ) : null}
                    {task.track ? (
                      <span
                        className="rounded-full px-2 py-0.5 text-[10.5px] font-bold"
                        style={{
                          backgroundColor: (task.track.color || '#1C3B9B') + '18',
                          color: task.track.color || '#1C3B9B',
                        }}
                      >
                        {task.track.title}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-[11px] font-bold text-gray-500">
                    {task.session
                      ? 'جلسهٔ ' + fa(task.session.number) + ' · ' + task.session.title
                      : 'بدون جلسه'}
                    {' · '}
                    {fa(task.fields?.length ?? 0)} بخش پاسخ
                  </p>
                </div>

                {due ? (
                  <span
                    className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-bold"
                    style={{
                      backgroundColor: due.overdue ? '#FEF2F2' : due.soon ? '#FFFBEB' : '#F1F5F9',
                      color: due.overdue ? '#d03b3b' : due.soon ? '#B45309' : '#64748B',
                    }}
                  >
                    <CalendarClock className="h-3 w-3" />
                    {due.text}
                  </span>
                ) : null}
              </div>

              {total ? (
                <>
                  <div className="mt-3 flex h-2 gap-0.5 overflow-hidden rounded-full">
                    {STATUS.map((state) => {
                      const value = (task.myCounts as any)[state.key] ?? 0;
                      return value ? (
                        <div
                          key={state.key}
                          title={state.label + ': ' + fa(value)}
                          style={{
                            width: (value / total) * 100 + '%',
                            backgroundColor: state.color,
                          }}
                        />
                      ) : null;
                    })}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px] font-bold">
                    <span className="text-gray-500">{fa(total)} نفر از گروه شما</span>
                    {STATUS.map((state) => {
                      const value = (task.myCounts as any)[state.key] ?? 0;
                      return value ? (
                        <span key={state.key} style={{ color: state.color }}>
                          {fa(value)} {state.label}
                        </span>
                      ) : null;
                    })}
                  </div>
                </>
              ) : (
                <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-[11px] font-bold text-gray-400">
                  {task.isPublished
                    ? 'به هیچ‌کدام از شرکت‌کنندگان شما تخصیص نیافته است.'
                    : 'هنوز منتشر نشده، پس به کسی تخصیص نیافته است.'}
                </p>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};
